import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import DriverPerformanceHeatmap from '../components/DriverPerformanceHeatmap';
import {
  ArrowLeft, Truck, Users, MapPin, DollarSign, Activity, TrendingUp, CheckCircle2, Download, ShieldAlert,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  Assigned: '#FF9500',
  'In Transit': '#002FA7',
  Delivered: '#34C759',
};

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtShortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const csvEscape = (val) => {
  if (val === null || val === undefined) return '';
  const s = String(val);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const rowsToCsv = (headers, rows) =>
  [headers.map(csvEscape).join(','), ...rows.map((r) => r.map(csvEscape).join(','))].join('\n');

const buildAnalyticsCsv = (data) => {
  const today = new Date().toISOString().slice(0, 10);
  const lines = [];
  lines.push(`CivilX TMS Analytics Report,${today}`);
  lines.push('');
  lines.push('# KPIs');
  lines.push(
    rowsToCsv(
      ['Metric', 'Value'],
      Object.entries(data.kpis).map(([k, v]) => [k.replace(/_/g, ' '), v]),
    ),
  );
  lines.push('');
  lines.push('# Load Status Mix');
  lines.push(
    rowsToCsv(
      ['Status', 'Count'],
      data.status_breakdown.map((s) => [s.name, s.value]),
    ),
  );
  lines.push('');
  lines.push('# Loads Delivered — Last 30 Days');
  lines.push(
    rowsToCsv(
      ['Date', 'Delivered', 'Miles'],
      data.loads_timeline.map((t) => [t.date, t.delivered, t.miles]),
    ),
  );
  lines.push('');
  lines.push('# Top Drivers');
  lines.push(
    rowsToCsv(
      ['Driver', 'Completed Loads', 'Total Miles'],
      data.top_drivers.map((d) => [d.name, d.completed_loads, d.total_miles]),
    ),
  );
  lines.push('');
  lines.push('# Payroll Trends');
  lines.push(
    rowsToCsv(
      ['Week Start', 'Week End', 'Drivers', 'Gross', 'Net'],
      data.payroll_trends.map((p) => [p.week_start, p.week_end, p.drivers_count, p.gross, p.net]),
    ),
  );
  return lines.join('\n');
};

const downloadCsv = (filename, content) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const KpiCard = ({ testid, icon: Icon, label, value, color = '#002FA7', onClick }) => {
  const Component = onClick ? 'button' : 'div';
  return (
    <Component
      data-testid={testid}
      onClick={onClick}
      className={`bg-white border border-slate-200 rounded-md p-6 w-full text-left ${onClick ? 'cursor-pointer hover:border-[#002FA7] hover:shadow-sm transition-all' : ''}`}
    >
      <div className="flex items-center gap-3">
        <Icon className="w-8 h-8" style={{ color }} strokeWidth={2} />
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{label}</p>
          <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{value}</p>
        </div>
      </div>
    </Component>
  );
};

const Analytics = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/analytics/overview`, { withCredentials: true });
        if (mounted) setData(res.data);
      } catch (e) {
        if (mounted) setError(e.response?.data?.detail || 'Failed to load analytics');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return <DashboardLayout><div className="p-8 text-slate-600">Loading analytics...</div></DashboardLayout>;
  }
  if (error) {
    return <DashboardLayout><div className="p-8 text-red-600" data-testid="analytics-error">{error}</div></DashboardLayout>;
  }
  if (!data) return null;

  const { kpis, status_breakdown, loads_timeline, top_drivers, payroll_trends } = data;
  const timelineForChart = loads_timeline.map((t) => ({ ...t, label: fmtShortDate(t.date) }));
  const payrollForChart = payroll_trends.map((p) => ({
    ...p,
    label: p.week_start ? fmtShortDate(p.week_start) : 'Wk',
  }));

  const utilizationPct = kpis.active_drivers > 0
    ? Math.round((kpis.drivers_on_road / kpis.active_drivers) * 100)
    : 0;

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="analytics-page">
        <button
          data-testid="back-to-dashboard"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2} />
          Back to dashboard
        </button>

        <div className="flex items-start justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Reports & Analytics
            </h1>
            <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              KPIs, load activity, driver performance and payroll trends for your fleet.
            </p>
          </div>
          <button
            data-testid="export-csv-button"
            onClick={() => {
              const csv = buildAnalyticsCsv(data);
              const stamp = new Date().toISOString().slice(0, 10);
              downloadCsv(`civilx-tms-analytics-${stamp}.csv`, csv);
            }}
            className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all duration-200 whitespace-nowrap"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            <Download className="w-4 h-4" strokeWidth={2} />
            Download CSV
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <KpiCard testid="kpi-total-loads" icon={Truck} label="Total Loads" value={kpis.total_loads} onClick={() => navigate('/loads')} />
          <KpiCard testid="kpi-delivered-loads" icon={CheckCircle2} label="Delivered" value={kpis.delivered_loads} color="#34C759" onClick={() => navigate('/loads?status=delivered')} />
          <KpiCard testid="kpi-miles-delivered" icon={MapPin} label="Miles Delivered" value={kpis.total_miles_delivered.toLocaleString()} color="#FF9500" onClick={() => navigate('/loads?status=delivered')} />
          <KpiCard testid="kpi-active-drivers" icon={Users} label="Active Drivers" value={kpis.active_drivers} onClick={() => navigate('/drivers?status=active')} />
          <KpiCard testid="kpi-drivers-on-road" icon={Activity} label="Drivers on Road" value={`${kpis.drivers_on_road} / ${kpis.active_drivers}`} color="#002FA7" onClick={() => navigate('/loads?status=in_transit')} />
          <KpiCard testid="kpi-utilization" icon={TrendingUp} label="Utilization" value={`${utilizationPct}%`} color="#34C759" onClick={() => navigate('/loads?status=in_transit')} />
          <KpiCard testid="kpi-pending-drivers" icon={Users} label="Pending Approval" value={kpis.pending_drivers} color="#FF9500" onClick={() => navigate('/drivers?status=registered')} />
          <KpiCard testid="kpi-latest-net" icon={DollarSign} label="Latest Payroll Net" value={fmtMoney(kpis.latest_payroll_net)} color="#002FA7" onClick={() => navigate('/payroll')} />
          <KpiCard
            testid="kpi-compliance-risk"
            icon={ShieldAlert}
            label="Compliance Risk"
            value={`${kpis.compliance_risk_loads ?? 0}${(kpis.pod_reminders_fired ?? 0) > 0 ? ` (${kpis.pod_reminders_fired} POD nudges)` : ''}`}
            color={(kpis.compliance_risk_loads ?? 0) > 0 ? '#DC2626' : '#34C759'}
            onClick={() => navigate('/loads?compliance=missing_docs')}
          />
        </div>

        {/* Charts row 1: timeline + pie */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div data-testid="loads-timeline-chart" className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-6">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Loads Delivered — Last 30 Days
              </h2>
              <button data-testid="open-loads-link" onClick={() => navigate('/loads?status=delivered')} className="text-xs font-semibold text-[#002FA7] hover:underline" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>View loads →</button>
            </div>
            <p className="text-xs text-slate-500 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Daily count of loads marked delivered
            </p>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={0}>
                <LineChart data={timelineForChart} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748B' }} interval={3} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748B' }} />
                  <Tooltip
                    contentStyle={{ border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 12 }}
                    formatter={(v, n) => [v, n === 'delivered' ? 'Delivered' : 'Miles']}
                  />
                  <Line type="monotone" dataKey="delivered" stroke="#002FA7" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div data-testid="status-breakdown-chart" className="bg-white border border-slate-200 rounded-md p-6">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Load Status Mix
              </h2>
              <button data-testid="open-loads-all-link" onClick={() => navigate('/loads')} className="text-xs font-semibold text-[#002FA7] hover:underline" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>View all →</button>
            </div>
            <p className="text-xs text-slate-500 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Click a slice to filter loads by status
            </p>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={0}>
                <PieChart>
                  <Pie
                    data={status_breakdown}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                    onClick={(entry) => {
                      const status = (entry?.name || '').toLowerCase().replace(/ /g, '_');
                      if (status) navigate(`/loads?status=${status}`);
                    }}
                    cursor="pointer"
                  >
                    {status_breakdown.map((entry) => (
                      <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || '#94A3B8'} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Charts row 2: top drivers + payroll trends */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div data-testid="top-drivers-chart" className="bg-white border border-slate-200 rounded-md p-6">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Top Drivers by Completed Loads
              </h2>
              <button data-testid="open-drivers-link" onClick={() => navigate('/drivers')} className="text-xs font-semibold text-[#002FA7] hover:underline" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>View drivers →</button>
            </div>
            <p className="text-xs text-slate-500 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Click a bar to open that driver's page
            </p>
            {top_drivers.length === 0 ? (
              <p className="text-sm text-slate-500 py-12 text-center">No delivered loads yet.</p>
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={0}>
                  <BarChart data={top_drivers} layout="vertical" margin={{ top: 8, right: 24, left: 24, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#64748B' }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#64748B' }} width={120} />
                    <Tooltip
                      contentStyle={{ border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 12 }}
                      formatter={(v, n) => [v, n === 'completed_loads' ? 'Loads' : 'Miles']}
                    />
                    <Bar
                      dataKey="completed_loads"
                      fill="#002FA7"
                      radius={[0, 4, 4, 0]}
                      cursor="pointer"
                      onClick={(entry) => entry?.driver_user_id && navigate(`/drivers?highlight=${entry.driver_user_id}`)}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div data-testid="payroll-trends-chart" className="bg-white border border-slate-200 rounded-md p-6">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Payroll Trends
              </h2>
              <button data-testid="open-payroll-link" onClick={() => navigate('/payroll')} className="text-xs font-semibold text-[#002FA7] hover:underline" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>View payroll →</button>
            </div>
            <p className="text-xs text-slate-500 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Gross vs Net across recent payroll runs
            </p>
            {payroll_trends.length === 0 ? (
              <p className="text-sm text-slate-500 py-12 text-center">No payroll runs yet.</p>
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={0}>
                  <BarChart data={payrollForChart} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748B' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip
                      contentStyle={{ border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 12 }}
                      formatter={(v, n) => [fmtMoney(v), n === 'gross' ? 'Gross' : 'Net']}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="gross" name="Gross" fill="#94A3B8" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="net" name="Net" fill="#002FA7" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Top drivers table */}
        <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="top-drivers-table">
          <h2 className="text-lg font-semibold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Driver Performance
          </h2>
          {top_drivers.length === 0 ? (
            <p className="text-sm text-slate-500">No completed loads recorded.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Driver</th>
                    <th className="text-right py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Completed</th>
                    <th className="text-right py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Total Miles</th>
                  </tr>
                </thead>
                <tbody>
                  {top_drivers.map((d) => (
                    <tr
                      key={d.driver_user_id}
                      data-testid={`driver-perf-row-${d.driver_user_id}`}
                      onClick={() => navigate(`/drivers?highlight=${d.driver_user_id}`)}
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    >
                      <td className="py-3 px-2 text-sm text-slate-900 font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{d.name}</td>
                      <td className="py-3 px-2 text-sm text-slate-600 text-right" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{d.completed_loads}</td>
                      <td className="py-3 px-2 text-sm text-slate-600 text-right" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{d.total_miles.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-10 pt-10 border-t border-slate-200">
          <DriverPerformanceHeatmap />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Analytics;
